import React, { useState } from 'react'
import { useFetchApiObject } from '@e3dc-react/shell/Hooks/useFetch'
import { Table } from '@e3dc-react/shell/Elements/Table/Table'
import Loading from '@e3dc-react/shell/Elements/Loading/Loading'
import CompanyShowRoomsEmptyView from 'src/Libs/company-settings/components/showrooms/CompanyShowRoomsEmptyView'
import { ShowRoom } from '../../models/showroom.model'
import useShowroomTableColumns from 'src/Libs/company-settings/components/showrooms/use-showroom-table-columns'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, useMediaQuery, useTheme } from '@mui/material'
import { Breakpoints } from 'src/Configuration/breakpoints'
import { BlankViewImageVariants, BlankView, BlankViewImageSizes } from 'src/Components/blank-view'
import { J2Add } from '@e3dc-react/icons'
import useModals from 'src/Hooks/useModals'
import { useTypedSelector } from 'src/State/RootReducer'
import { useDispatch } from 'react-redux'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { useCookies } from 'src/Libs/cookies'
import GoogleAgreement from './GoogleAgreement'

type Props = {
  companyId: number
}

/**
 * Displays the fetched showrooms table
 */
const CompanyShowRooms: React.FC<Props> = ({ companyId }) => {
  const { t } = useCustomTranslation()
  const dispatch = useDispatch()
  const [isDeleteLoading, setDeleteLoading] = useState(false)
  const [isError, setError] = useState(false)
  const refresh = useTypedSelector(state => state.apiView.refresh)
  const { columnsLg, columnsSm } = useShowroomTableColumns(companyId, setDeleteLoading, setError)
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down(Breakpoints.Mobile))
  const { openCompanyShowroomModal } = useModals()
  const { cookies } = useCookies()
  const { googleAgreed } = cookies

  const [showRooms, isFetchLoading, isFetchError] = useFetchApiObject<ShowRoom[]>({
    url: `users/companies/${companyId}/showrooms`,
    dependencies: [refresh],
  })

  if (isFetchError || isError) {
    return (
      <Box>
        <BlankView
          imageVariant={BlankViewImageVariants.Worried}
          imageSize={BlankViewImageSizes.Small}
          title={'common.error'}
          explanation={'administration.companyManagement.showroom.error-view.explanation'}
        />
      </Box>
    )
  }

  const columns = downSm ? columnsSm : columnsLg

  if (isFetchLoading || isDeleteLoading) {
    return <Loading />
  } else if (!showRooms || showRooms.length === 0) {
    return <CompanyShowRoomsEmptyView companyId={companyId} googleAgreed={googleAgreed ?? false} />
  }

  return (
    <>
      <Table
        header={columns.map(c => ({
          key: c.name,
          label: c.label,
        }))}
        data={showRooms.map(r => columns.map(c => c.value(r)))}
      />
      {googleAgreed ? (
        <Button
          sx={{ marginTop: 2 }}
          startIcon={<J2Add />}
          variant="outlined"
          onClick={() => {
            openCompanyShowroomModal({
              entityAccess: 'create',
              onSave: () => dispatch(refreshApiView()),
              data: null,
              companyId: companyId,
            })
          }}
        >
          {t('administration.companyManagement.showroom.button-text')}
        </Button>
      ) : (
        <GoogleAgreement />
      )}
    </>
  )
}

export default CompanyShowRooms
